import Vue from 'vue'
import Vuex from 'vuex'
import { setLocale } from '@/i18n'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    user: JSON.parse(localStorage.getItem('opcardx_user')) || null,
    token: localStorage.getItem('opcardx_token') || null,
    locale: localStorage.getItem('opcardx_lang') || 'en',
    currency: localStorage.getItem('opcardx_currency') || 'USD',
    currencySymbol: localStorage.getItem('opcardx_currency_symbol') || '$',
    collection: [],
    lastScanResults: JSON.parse(localStorage.getItem('opcardx_scan_results') || '[]'),
    lastScanState: localStorage.getItem('opcardx_scan_state') || 'idle',
    // 汇率：以 USD 为基准，其他币种对 USD 的汇率
    // 例如 { USD: 1, HKD: 7.8, JPY: 150 }
    exchangeRates: JSON.parse(localStorage.getItem('opcardx_exchange_rates') || 'null') || { USD: 1, HKD: 7.8, JPY: 150 },
    exchangeRatesUpdatedAt: localStorage.getItem('opcardx_exchange_rates_at') || null,
  },

  getters: {
    isLoggedIn: state => !!state.token && !!state.user,
    currentUser: state => state.user,
    token: state => state.token,
    collection: state => state.collection,
    lastScanResults: state => state.lastScanResults,
    lastScanState: state => state.lastScanState,
    locale: state => state.locale,
    currency: state => state.currency,
    currencySymbol: state => state.currencySymbol || '$',
    exchangeRates: state => state.exchangeRates,
    // 当前币种对 USD 的汇率
    currentRate: state => {
      const rates = state.exchangeRates || { USD: 1, HKD: 7.8, JPY: 150 }
      return rates[state.currency] || 1
    },
  },

  mutations: {
    SET_USER(state, user) {
      state.user = user
      if (user) {
        localStorage.setItem('opcardx_user', JSON.stringify(user))
      } else {
        localStorage.removeItem('opcardx_user')
      }
    },

    SET_TOKEN(state, token) {
      state.token = token
      if (token) {
        localStorage.setItem('opcardx_token', token)
        localStorage.setItem('opcardx_token_time', Date.now().toString())
      } else {
        localStorage.removeItem('opcardx_token')
      }
    },

    CLEAR_AUTH(state) {
      state.user = null
      state.token = null
      state.collection = []
      state.lastScanResults = []
      state.lastScanState = 'idle'
      localStorage.removeItem('opcardx_token')
      localStorage.removeItem('opcardx_user')
      localStorage.removeItem('opcardx_scan_results')
      localStorage.removeItem('opcardx_scan_state')
    },

    SET_LOCALE(state, lang) {
      state.locale = lang
      setLocale(lang)
    },

    SET_CURRENCY(state, { value, symbol }) {
      state.currency = value
      state.currencySymbol = symbol
      localStorage.setItem('opcardx_currency', value)
      localStorage.setItem('opcardx_currency_symbol', symbol)
    },

    SET_COLLECTION(state, list) {
      state.collection = list
    },

    UPDATE_CARD_OWNED(state, { cardId, owned }) {
      const card = state.collection.find(c => c.id === cardId)
      if (card) {
        card.owned = owned
        if (owned <= 0) {
          state.collection = state.collection.filter(c => c.id !== cardId)
        }
      } else if (owned > 0) {
        state.collection.push({ id: cardId, owned })
      }
    },

    SET_SCAN_RESULTS(state, { results, scanState }) {
      state.lastScanResults = results
      state.lastScanState = scanState
      localStorage.setItem('opcardx_scan_results', JSON.stringify(results))
      localStorage.setItem('opcardx_scan_state', scanState)
    },

    SET_EXCHANGE_RATES(state, rates) {
      state.exchangeRates = rates
      state.exchangeRatesUpdatedAt = Date.now().toString()
      localStorage.setItem('opcardx_exchange_rates', JSON.stringify(rates))
      localStorage.setItem('opcardx_exchange_rates_at', state.exchangeRatesUpdatedAt)
    },
  },

  actions: {
    login({ commit }, { user, token }) {
      commit('SET_USER', user)
      commit('SET_TOKEN', token)
    },

    logout({ commit }) {
      commit('CLEAR_AUTH')
    },

    switchLocale({ commit }, lang) {
      commit('SET_LOCALE', lang)
    },

    switchCurrency({ commit }, { value, symbol }) {
      commit('SET_CURRENCY', { value, symbol })
    },

    updateCardOwned({ commit }, payload) {
      commit('UPDATE_CARD_OWNED', payload)
    },

    saveScanResults({ commit }, { results, scanState }) {
      commit('SET_SCAN_RESULTS', { results, scanState })
    },

    /**
     * 从 open.er-api.com 获取实时汇率（以 USD 为基准）
     * 每小时最多请求一次，缓存到 localStorage
     */
    async fetchExchangeRates({ commit, state }) {
      // 如果缓存未超过 1 小时，直接跳过
      const ONE_HOUR = 60 * 60 * 1000
      const updatedAt = state.exchangeRatesUpdatedAt
      if (updatedAt && Date.now() - Number(updatedAt) < ONE_HOUR) return

      try {
        const res = await fetch('https://open.er-api.com/v6/latest/USD')
        const json = await res.json()
        if (json && json.rates) {
          // 只保留我们需要的币种
          const { USD = 1, HKD = 7.8, JPY = 150 } = json.rates
          commit('SET_EXCHANGE_RATES', { USD, HKD, JPY })
        }
      } catch (e) {
        // 请求失败时保持现有汇率，不报错
        console.warn('[ExchangeRate] fetch failed, using cached rates', e)
      }
    },
  },
})
